import { useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { UiKit } from '@datavant/uikit';
import { tRPCProxyClient } from '../../utils/trpc';
import { isAccessToken, SessionObject } from '@okta/okta-auth-js';
import { useRouter } from 'next/router';

/**
 * Time left before expiry when we automatically try to refresh the token/session
 */
const REFRESH_IF_TIME_REMAINING_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Only automatically refresh the token/session if the user has performed an action
 * within this time period
 */
const REFRESH_IF_LAST_ACTION_WITHIN_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Time remaining when we show the modal warning of expiry
 *
 * This should be < REFRESH_IF_TIME_REMAINING_MS, because otherwise it pops
 * up briefly while the refresh is already happening automatically anyways
 */
const COUNT_DOWN_MILLISECONDS = 3 * 60 * 1000; // 3 minutes

const TokenExpiryModal: React.FC = () => {
    const { oktaAuth, authState } = useOktaAuth();
    const router = useRouter();
    const [refresh, setRefresh] = useState<number>(0);
    const [sessionExpirationTime, setSessionExpirationTime] = useState<number>(
        Date.now() + REFRESH_IF_LAST_ACTION_WITHIN_MS
    );
    const [tokenExpirationTime, setTokenExpirationTime] = useState<number>(
        Date.now() + REFRESH_IF_LAST_ACTION_WITHIN_MS
    );
    const [lastActionTime, setLastActionTime] = useState<number>(Date.now());

    useEffect(() => {
        const actionPerformed = () => {
            setLastActionTime(Date.now());
        };

        const events = ['load', 'mousedown', 'click', 'keypress'];
        events.forEach(event => {
            window.addEventListener(event, actionPerformed);
        });

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, actionPerformed);
            });
        };
    }, []);

    const checkTokenExpiry = useCallback(async () => {
        const token = await oktaAuth.tokenManager.get('accessToken');
        if (token && token.expiresAt) {
            const expirationTime = token.expiresAt * 1000; // Convert to milliseconds
            setTokenExpirationTime(expirationTime);
        }

        const session = (await oktaAuth.session.get()) as SessionObject & {
            expiresAt: string | undefined;
        };
        if (session && session.expiresAt) {
            const sessionExpirationTime = new Date(session.expiresAt);
            setSessionExpirationTime(sessionExpirationTime.getTime());
        }
    }, [oktaAuth]);

    useEffect(() => {
        // Start the check when authenticated
        if (authState && authState.isAuthenticated) {
            void checkTokenExpiry();
        }
    }, [authState, oktaAuth, checkTokenExpiry]);

    const sessionTimeRemaining = sessionExpirationTime - Date.now();
    const tokenTimeRemaining = tokenExpirationTime - Date.now();
    const minimumTimeRemaining = Math.min(sessionTimeRemaining, tokenTimeRemaining);
    const timeSinceLastAction = Date.now() - lastActionTime;

    const handleLogOut = useCallback(async () => {
        await router.push('/logout');
    }, [router]);

    useEffect(
        () =>
            void (async () => {
                if (minimumTimeRemaining < 0) {
                    await handleLogOut();
                }

                // If there was a recent action
                if (timeSinceLastAction < REFRESH_IF_LAST_ACTION_WITHIN_MS) {
                    // If the session is about to expire
                    if (sessionTimeRemaining < REFRESH_IF_TIME_REMAINING_MS) {
                        await handleSessionRefresh();
                    }

                    // If the token is about to expire
                    if (tokenTimeRemaining < REFRESH_IF_TIME_REMAINING_MS) {
                        await handleTokenRefresh();
                    }
                }

                setTimeout(() => {
                    setRefresh(old => old + 1);
                }, 1000);
            })(),
        [refresh]
    );

    const handleSessionRefresh = useCallback(async () => {
        try {
            await oktaAuth.session.refresh();
            await checkTokenExpiry();
        } catch (error) {
            console.error('Error refreshing session:', error);
            await handleLogOut(); // Log out if session refresh fails
        }
    }, [oktaAuth, checkTokenExpiry, handleLogOut]);

    const handleTokenRefresh = useCallback(async () => {
        try {
            const refreshedToken = await oktaAuth.tokenManager.renew('accessToken');
            // If token refresh successful, update server session
            if (!refreshedToken || !isAccessToken(refreshedToken)) {
                throw new Error('No access token or user is not authenticated');
            }
            await tRPCProxyClient.session.createUserSession.mutate({
                accessToken: refreshedToken.accessToken,
            });
        } catch (error) {
            console.error('Error refreshing token:', error);
            await handleLogOut(); // Log out if token refresh fails
        }
    }, [handleLogOut, oktaAuth]);

    const refreshAll = useCallback(async () => {
        await handleSessionRefresh();
        await handleTokenRefresh();
    }, [handleSessionRefresh, handleTokenRefresh]);

    const bodyFragment: JSX.Element = (
        <UiKit.Chakra.VStack key={'token-expiry-body'} align={'left'} width={'400px'}>
            <UiKit.Text.BodyRegular>
                Your session will expire in {Math.floor(minimumTimeRemaining / 1000)} seconds due to
                inactivity. If you'd like to continue, please click “Stay Logged In” to keep your session
                active.
            </UiKit.Text.BodyRegular>
            <UiKit.Text.BodyRegular>
                Otherwise, you’ll be logged out and redirected to the login page.
            </UiKit.Text.BodyRegular>
        </UiKit.Chakra.VStack>
    );

    const footerFragment: JSX.Element = (
        <UiKit.Chakra.HStack key={'token-expiry-footer'}>
            <UiKit.Button.Primary text={'Stay Logged In'} onClick={refreshAll} />
        </UiKit.Chakra.HStack>
    );

    return (
        <UiKit.Modal
            title={'Session About to Expire'}
            bodyFragment={bodyFragment}
            footerFragment={footerFragment}
            isOpen={minimumTimeRemaining <= COUNT_DOWN_MILLISECONDS}
            onClose={refreshAll}
            size={'md'}
        />
    );
};

export default TokenExpiryModal;
